import {FC, useContext, useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Link, useNavigate} from "react-router-dom";

import FooterText from "../../components/footerText";
// import ContactAndSafetyInfo from "../../components/contactAndSafetyInfo";
// import { TextAlignProps } from "../../components/contactAndSafetyInfo/types";
import HubInputField from "../../components/inputType";

import {AuthContext} from "../../context/authContext";
import {ToastContext} from "../../context/toastContext";

import {LoginSchema} from "../../validation/login";
import useFetch from "../../hooks/useFetch";
import {APIEndpoints} from "../../types/apiTypes";
import {LoginAPIResponseType, LoginFormType} from "./types";
import {ToastTypes} from "../../types";
import {UserType} from "src/types/users";
import {USER_TYPES} from "src/constants";
import ContactAndSafetyInfo from "../../components/contactAndSafetyInfo";
import {TextAlignProps} from "../../components/contactAndSafetyInfo/types";
import {BVLogo} from "../../assets";

const HubLogin: FC = (): JSX.Element => {
  const { control, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(LoginSchema),
  });
  const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);

  const { userData: sessionSavedUser, createSession, restoreSession } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const reRouteRelevantUser = (userData: UserType) => {
    if (!userData.is_password_changed) {
      navigate("/users/createuser");
    } else {
     if (userData.ref_type === 'Manufacturer') {
        navigate("/statistics");
      } else if (userData.ref_type === "rep") {
        navigate(`/territories`);
      } else if (USER_TYPES.HCP.includes(userData.ref_type)) {
        navigate("/cases");
      } else {
        navigate("/cases");
      }
    }
  };

  const { data: userData, loading, mutate, error, removeErrorAndData } = useFetch<LoginAPIResponseType>(APIEndpoints.USER_SIGNIN);

  useEffect(() => {
    if (userData && userData.status.code === 200) {
      const { status, token } = userData;
      createSession(
        {
          ...status.data.user,
          practice_created_date: status.data.address ? status.data.address.date : "",
          practice_name: status.data.address ? status.data.address.name : "",
          reset_demo_cases: status.data.navbar.reset_demo_cases,
          address: status.data.address ? status.data.address.ref_address : ""
        },
        status.data.sub_header,
        token
      );
      showToast([status.message], ToastTypes.INFO);

      reRouteRelevantUser(status.data.user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const submitForm = async (values: LoginFormType) => {
    if ((error && error.length > 0) || userData) removeErrorAndData();
    const isSessionExists = restoreSession();

    if (!isSessionExists) {
      mutate({ user: values });
    } else {
      showToast([`There is already an active user session`], ToastTypes.INFO);
      if (sessionSavedUser) reRouteRelevantUser(sessionSavedUser);
    }
  };

  return (
    <div className="container-fluid login-sec d-flex flex-fill flex-column justify-content-between overflow-auto">
      <div className="container d-flex flex-fill flex-column py-4 px-0 py-lg-4 px-lg-4">
        <ContactAndSafetyInfo alignText={TextAlignProps.TEXT_CENTER} textColor={"black"} />
        <div className="row align-items-center flex-fill mt-3">
          <div className="col-lg-7 col-12">
            <form className="card mb-3 mb-sm-5 mb-md-5" onSubmit={handleSubmit(submitForm)}>
              <div className="card-header">
                <h2 className="m-0">Welcome to</h2>
                <h1 className="m-0 mb-2 fw-bold">StimRouter BV360</h1>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label className="mb-1">Email *</label>
                  <Controller
                      name="email"
                      control={control}
                      defaultValue=""
                      render={({field, fieldState: {error}}) => (
                          <HubInputField
                              inputId="email"
                              inputPlaceholder="Email"
                              isRowBased={false}
                              isRequired={true}
                              field={field}
                              error={error}
                              inputType="email"
                          />
                      )}
                  />
                </div>
                <label className="mb-1">Password *</label>
                <Controller name="password" control={control} defaultValue=""
                            render={({field, fieldState: {error}}) => (
                                <>
                                  <div className="input-group mb-3">
                                    <input
                                        type={isPasswordVisible ? "text" : "password"} placeholder="Password"
                                        className="form-control" {...field} />
                                    <button type="button" className="btn btn-primary"
                                            onClick={() => setPasswordVisibility((isPasswordVisible) => !isPasswordVisible)}
                                    >
                                      {isPasswordVisible ? (
                                          <i className="bi bi-eye-fill"></i>
                                      ) : (
                                          <i className="bi bi-eye-slash"></i>
                                      )}
                                    </button>
                                  </div>
                                  {error && (<p className="mb-0 text-danger">{error.message}</p>)}
                                </>
                            )}
                />
              </div>
              <div className="card-footer bg-transparent border-0 pt-0">
                <button disabled={loading} className="btn btn-primary me-1" type="submit">
                  {loading ? (<i className="spinner-border"></i>) : null} Log in
                </button>
                <Link to="/enrollments/new" className="btn btn-primary">Enroll</Link>
                <p className="mb-2 mt-1">
                  <Link to="/users/forgotpassword" className="text-decoration-none">
                    Forgot your password?
                  </Link>
                </p>
              </div>
            </form>
            <div className="bg-white p-md-0 p-3 rounded-3">
              <div className="row">
                <div className="col-12 col-lg-3">
                  <img className="img-fluid" width={180} src={BVLogo} alt="Products Logos"/>
                </div>
                <p className="copyright-text col m-0">
                  This site is intended for US health care professionals. StimRouter is a registered trademarks of
                  Bioness
                  Inc.
                  All other trademarks are property of their respective owners. ©2021 Bioventus LLC
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubLogin;
